import { PaymentService, PaymentStatusResponseDto, InvoicePayment, TransactionType, TerminalMode } from "@services";
import { makeAutoObservable, runInAction } from 'mobx';
import { L } from '@lib';
import { Utils } from '@utils';

class PaymentStore {
  loading: boolean = false;
  isTerminalEnabled: boolean = false;

  // Payment processing properties
  processingPayment: boolean = false;
  processingStatus: PaymentStatusResponseDto | null = null;
  paymentFinished: boolean = false;
  trackingId: string | null = null;
  cardPaymentLoading: boolean = false;
  cashPaymentLoading: boolean = false;
  isProcessingPaymentRef: boolean = false;

  // Private properties for intervals
  private pollingTimeout: NodeJS.Timeout | null = null;
  private closeTimeout: NodeJS.Timeout | null = null;
  // Non-observable flag — stops polling loop. Must NOT be tracked by MobX.
  private _pollingCancelled = false;

  constructor() {
    makeAutoObservable(this);
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setTerminalEnabled = (value: boolean) => {
    runInAction(() => {
      this.isTerminalEnabled = value;
    });
  };

  checkTerminalStatus = async () => {
    this.setLoading(true);
    try {
      const terminalEnabled = await PaymentService.hasTerminalEnabled();
      this.setTerminalEnabled(terminalEnabled);
      return terminalEnabled;
    } catch (error) {
      console.error('Failed to check terminal status:', error);
      this.setTerminalEnabled(false);
      return false;
    } finally {
      this.setLoading(false);
    }
  };

  clearTerminalStatus = () => {
    this.resetPaymentState();
    runInAction(() => {
      this.isTerminalEnabled = false;
      this.loading = false;
    });
  };

  setProcessingPayment = (value: boolean) => {
    runInAction(() => {
      this.processingPayment = value;
    });
  };

  setProcessingStatus = (status: PaymentStatusResponseDto | null) => {
    runInAction(() => {
      this.processingStatus = status;
    });
  };

  setPaymentFinished = (value: boolean) => {
    runInAction(() => {
      this.paymentFinished = value;
    });
  };

  setTrackingId = (id: string | null) => {
    runInAction(() => {
      this.trackingId = id;
    });
  };

  setCardPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cardPaymentLoading = value;
    });
  };

  setCashPaymentLoading = (value: boolean) => {
    runInAction(() => {
      this.cashPaymentLoading = value;
    });
  };

  setIsProcessingPaymentRef = (value: boolean) => {
    runInAction(() => {
      this.isProcessingPaymentRef = value;
    });
  };

  get hasPaymentError() {
    return this.processingStatus?.status === 'FAILED' || this.processingStatus?.status === 'ERROR' || this.processingStatus?.status === 'TIMEOUT';
  }

  get signatureRequired(){
    return this.processingStatus?.status === 'SignatureVerificationRequired';
  }

  // Clear all intervals and timeouts
  clearIntervals = () => {
    this._pollingCancelled = true;
    if (this.pollingTimeout) {
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = null;
    }
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  };

  resetPaymentState = () => {
    this._pollingCancelled = true;
    runInAction(() => {
      this.processingPayment = false;
      this.processingStatus = null;
      this.paymentFinished = false;
      this.trackingId = null;

      this.cardPaymentLoading = false;
      this.cashPaymentLoading = false;
      this.isProcessingPaymentRef = false;
    });
    this.clearIntervals();
  };

  verifySignature = async (trackingId: string, isValid: boolean) => {
  try {
    console.log(`Verifying signature for tracking ID: ${trackingId}, valid: ${isValid}`);
    const result = await PaymentService.verifySignature({ trackingId, isValid });
    return result;
  } catch (error) {
    setTimeout(() => this.resetPaymentState(), 3000);
  }
};
  processCardPayment = async (
    invoiceId: string, 
    amount: number, 
    terminalMode: TerminalMode,
    onPaymentSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => {
    this.resetPaymentState();
    this.setCardPaymentLoading(true);
    this.setProcessingStatus(null);
    
    try {
      const roundedAmount = Utils.roundToTwoDecimals(amount);

      //investigate why it fails with InvoicePayment type
      const originalPaymentValues: any = {
          amount: roundedAmount,
          method: TerminalMode.Sale ? 'Card' : 'CardRefund',
          //note: "",
          transactionType: TransactionType.Payment,
          //id: "",
          //invoiceCreditNoteId: "",
          invoiceId: invoiceId,
          //creationTime: new Date(),
         // createdBy: ""
      };

      this.setIsProcessingPaymentRef(false);
      this.setProcessingPayment(true);
      this.setProcessingStatus({
        status: 'INITIALIZING',
        statusMessage: L('Initializing payment...'),
        order: 1,
      });
      this.setPaymentFinished(false);

      // Create tracking session
      const trackingResponse = await PaymentService.createPaymentTrackingSession({
        invoiceId,
        amount: roundedAmount
      });

      this.setTrackingId(trackingResponse.id);

      // Start payment processing (don't await)
      PaymentService.processPayment({
        invoiceId,
        amount: roundedAmount,
        paymentTrackingId: trackingResponse.id,
        terminalMode: terminalMode
      });

      // Start polling for status
      this.startStatusPolling(trackingResponse.id, originalPaymentValues, onPaymentSuccess);

    } catch (error) {
      console.error("Payment initialization error:", error);
      this.setProcessingStatus({
        status: 'ERROR',
        statusMessage: L('Payment initialization failed. Please try again.'),
        order: this.processingStatus?.order || 1
      });
      this.setIsProcessingPaymentRef(false);
      this.setCardPaymentLoading(false);
      setTimeout(() => this.setProcessingPayment(false), 3000);
      setTimeout(() => this.resetPaymentState(), 3000);
    }
  };

  // Sequential status polling — only ONE request in-flight at a time.
  // Eliminates the race condition caused by setInterval stacking requests on slow networks.
  private startStatusPolling = (
    trackingId: string, 
    originalPaymentValues: InvoicePayment, 
    onPaymentSuccess: (paymentData: InvoicePayment) => Promise<void>
  ) => {
    this._pollingCancelled = false;

    const pollOnce = async () => {
      if (this._pollingCancelled) return;

      try {
        const statusResult = await PaymentService.getPaymentStatus(trackingId);

        // Check cancellation again after await (could have been cancelled while waiting)
        if (this._pollingCancelled) return;

        this.setProcessingStatus(statusResult);

        if (statusResult.status === "PAID") {
          this._pollingCancelled = true;
          this.setIsProcessingPaymentRef(true);
          this.setPaymentFinished(true);
          this.setCardPaymentLoading(false);

          try {
            await onPaymentSuccess(originalPaymentValues);
          } catch (error) {
            console.error('Error processing payment success:', error);
          }

          this.closeTimeout = setTimeout(() => {
            this.setProcessingPayment(false);
            this.setPaymentFinished(false);
            this.setIsProcessingPaymentRef(false);
            this.setTrackingId(null);
          }, 5000);

          return; // Done — do not schedule next poll
        }

        if (["FAILED", "ERROR", "TIMEOUT"].includes(statusResult.status)) {
          this._pollingCancelled = true;
          this.setIsProcessingPaymentRef(false);
          this.setCardPaymentLoading(false);
          this.setProcessingStatus({
            status: statusResult.status,
            statusMessage: statusResult.statusMessage || L("Payment failed"),
            order: statusResult.order
          });
          this.setPaymentFinished(true);
          return; // Done — do not schedule next poll
        }

        // Still processing — schedule next poll (only after current one completes)
        if (!this._pollingCancelled) {
          this.pollingTimeout = setTimeout(pollOnce, 500);
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
        // On network error, retry after delay unless cancelled
        if (!this._pollingCancelled) {
          this.pollingTimeout = setTimeout(pollOnce, 1000);
        }
      }
    };

    // Start first poll
    pollOnce();
  };

  // Cleanup method
  cleanup = () => {
    this.clearIntervals();
    this.resetPaymentState();
  };
}

export default PaymentStore;